/**
 * Onboard / Stage 2 — Profile heuristics.
 *
 * Runs entirely in the browser on captured HTML strings (rendered
 * HTML for browser engines, body preview for HTTP engines). No
 * extra network calls — everything operates on what /compare
 * already returned.
 *
 * Public API (all pure):
 *   detectListingCards(html)       → ListingCardSignal[]
 *   detectCookieBanner(html)       → CookieBannerHit | null
 *   detectJsRenderNeed(httpHtml, browserHtml) → JsRenderVerdict
 *   detectPagination(html, baseUrl) → PaginationHit | null
 *   recommendHumanevasionTier(verdict, blockSignals) → 'js'|'cdp'|'vm'
 *   buildProfile({...})            → SiteProfile  (the orchestrator)
 *
 * Each detector returns a structured result + a confidence score so
 * the UI can flag low-confidence guesses for manual review. The
 * detectors deliberately fall back to "null + reason string" rather
 * than throwing — UI shows "couldn't auto-detect" cleanly instead
 * of error-banner-on-the-page.
 */

// ── Types ─────────────────────────────────────────────────────────

export interface ListingCardSignal {
  selector:   string;     // CSS selector for the listing-card container
  tagName:    string;     // 'li' | 'div' | 'article' | etc
  count:      number;     // how many siblings of this shape were found
  confidence: number;     // 0-100
  reasons:    string[];   // human-readable signal evidence
  /** Inner-field selectors discovered by inspecting the first card of
   *  the winning group. Each is a CSS selector *relative to the card*.
   *  Empty string when the heuristic couldn't find a confident match
   *  — operator fills it in on the Stage-3 form. These power the
   *  cf_http_html template family which needs explicit child selectors
   *  (the docker_browser path uses just the container). */
  titleSelector?: string;
  priceSelector?: string;
  urlSelector?:   string;
  idSelector?:    string;
}

export interface CookieBannerHit {
  provider:   string;          // 'OneTrust' | 'Cookiebot' | 'TrustArc' | …
  acceptSelector: string;      // selector for the accept button
  containerSelector: string;   // selector for the banner container (for dismissal-not-found debugging)
  confidence: number;          // 0-100
}

export interface JsRenderVerdict {
  required:   boolean;
  reasons:    string[];
  /** ratio of browser HTML size to direct HTTP body size, where >1 means browser hydrated more content */
  sizeRatio:  number;
}

export interface PaginationHit {
  type:       'page-param' | 'offset-param' | 'cursor-param' | 'next-button' | 'load-more';
  paramName?: string;          // e.g. 'page', '_pgn', 'offset'
  selector?:  string;          // e.g. 'button.load-more'
  /** Offset-style page size (e.g. 24 → ?offset=24,48,72). Only set when
   *  `type === 'offset-param'` and we observed two distinct numeric
   *  values; undefined means "operator should set it in Stage 3". */
  multiplier?: number;
  confidence: number;
}

export type HumanevasionTier = 'js' | 'cdp' | 'vm';

/** Page-type the profile was built against. JSON profiles skip
 *  HTML-only heuristics (listing-card, cookie banner, JS-render) and
 *  populate the `json` block instead. */
export type ProfilePageType = 'html' | 'json';

export interface JsonProfile {
  /** Dot-path that resolved to a non-empty array (e.g. `response.data`).
   *  May be the operator-supplied path or, when omitted, the first
   *  array path the heuristic discovered. */
  listPath:   string;
  /** Number of items in the resolved array. */
  count:      number;
  /** Union of keys observed across the first ~5 items. Operators map
   *  these to title/price/image/url in Stage 3. */
  itemKeys:   string[];
  /** Best-guess field mapping — picks the most likely key for each
   *  product attribute. Operator can override in Stage 3. */
  fieldGuess: { title?: string; price?: string; image?: string; url?: string; id?: string };
  /** First item, JSON-stringified (truncated to 800 chars) — useful
   *  evidence for the operator before they wire field mappings. */
  sampleItem: string;
  /** Confidence 0-100. Penalised when path was guessed (not user-supplied)
   *  or when fewer than 3 items were observed. */
  confidence: number;
  reasons:    string[];
}

export interface SiteProfile {
  url:                   string;
  pageType:              ProfilePageType;
  recommendedEngine:     string;                  // EngineKind from probe-engines
  listingCard:           ListingCardSignal | null;
  cookieBanner:          CookieBannerHit | null;
  jsRenderNeed:          JsRenderVerdict;
  pagination:            PaginationHit | null;
  recommendedTier:       HumanevasionTier;
  /** Populated only when pageType === 'json'. Null otherwise. */
  json:                  JsonProfile | null;
  /** Coverage 0-100: how complete the auto-discovery is. Below 60 →
   *  manual review recommended. */
  completeness:          number;
}

// ── Listing-card detector ─────────────────────────────────────────

/**
 * Find repeated DOM elements that look like listing cards. Strategy:
 *   1. Group all elements by tag + leading class-name.
 *   2. Filter to groups with ≥3 siblings (single + tiny groups
 *      aren't grids).
 *   3. Score each candidate group on:
 *        - sibling count   (+1 per sibling, cap at +20)
 *        - keyword in classes (product/card/item/listing/tile/result/grid)
 *        - has child img   (+5)
 *        - has child link  (+5)
 *        - has price-like  text content (+10)
 *   4. Return top 3 candidates by score.
 *
 * Returns [] when no candidate groups exist. Caller's UI shows
 * "couldn't auto-detect — fill in manually" in that case.
 */
export function detectListingCards(html: string): ListingCardSignal[] {
  if (!html || html.length < 200) return [];
  const doc = parseHtml(html);
  if (!doc) return [];

  // Group by (tagName, leading class). Leading class is brittle for
  // sites with build-time hashed classes (e.g. styled-components
  // suffixes), but covers the common case where listing cards share
  // a stable BEM-style root class.
  type Group = { selector: string; tagName: string; els: Element[] };
  const groups = new Map<string, Group>();
  const candidates: Element[] = Array.from(
    doc.querySelectorAll('li, article, div[class], section[class]'),
  );
  for (const el of candidates) {
    const key = groupKey(el);
    if (!key) continue;
    const g = groups.get(key) ?? { selector: '', tagName: el.tagName.toLowerCase(), els: [] };
    g.els.push(el);
    groups.set(key, g);
  }

  const scored: ListingCardSignal[] = [];
  for (const [key, g] of groups) {
    if (g.els.length < 3) continue;
    const sample = g.els[0];
    const cls = sample.className || '';
    const reasons: string[] = [];
    let score = 0;

    // Sibling count — most weight.
    score += Math.min(g.els.length, 20) * 2;
    reasons.push(`${g.els.length} sibling ${g.tagName}s`);

    // Keyword hits in class.
    const lowerCls = cls.toLowerCase();
    const kw = ['product', 'card', 'item', 'listing', 'tile', 'result', 'grid-item', 'sku', 's-item', 's-card'];
    for (const w of kw) {
      if (lowerCls.includes(w)) {
        score += 12;
        reasons.push(`class contains "${w}"`);
        break;
      }
    }

    // Child checks — these are heuristics for "looks like a product card".
    if (sample.querySelector('img'))     { score += 5; reasons.push('has child img'); }
    if (sample.querySelector('a[href]')) { score += 5; reasons.push('has child link'); }
    if (containsPriceText(sample.textContent ?? '')) {
      score += 10; reasons.push('contains price-shaped text');
    }

    // Cap and normalise to 0-100.
    const confidence = Math.min(score, 100);
    const inner = detectInnerSelectors(sample);
    scored.push({
      selector:   key,
      tagName:    g.tagName,
      count:      g.els.length,
      confidence,
      reasons,
      titleSelector: inner.title,
      priceSelector: inner.price,
      urlSelector:   inner.url,
      idSelector:    inner.id,
    });
  }

  scored.sort((a, b) => b.confidence - a.confidence);
  return scored.slice(0, 3);
}

/** Heuristically pick title / price / url / id selectors *inside* one
 *  listing card. Returns CSS selectors relative to the card root.
 *
 *  Strategy per field:
 *    - title: prefer h1-h4 with non-empty text; fallback to class containing
 *             /name|title|description/ with longer (>3 word) text.
 *    - price: element with class containing /price|cost|amount|curprice/
 *             AND text matching `£/$/€` + digits; fallback to any text
 *             node that matches the currency regex via its parent class.
 *    - url:   first `<a href>` whose href looks like a product path
 *             (excludes wishlist / cart / login / account).
 *    - id:    data-*-id / data-sku attribute on the card root, else
 *             the trailing slug of the product link.
 *
 *  Every selector is the *first stable class chain* leading down to the
 *  match (e.g. `.productdescriptionname`) — anchoring to a single class
 *  is more robust than nth-of-type paths across siblings.
 */
function detectInnerSelectors(card: Element): {
  title: string; price: string; url: string; id: string;
} {
  const out = { title: '', price: '', url: '', id: '' };

  // ── title ──────────────────────────────────────────────────────
  for (const h of Array.from(card.querySelectorAll('h1,h2,h3,h4'))) {
    const txt = (h.textContent ?? '').trim();
    if (txt.length >= 4) {
      out.title = stableSelector(h, card);
      break;
    }
  }
  if (!out.title) {
    const NAME = /\b(name|title|description|product-?name|s-largered)\b/i;
    for (const el of Array.from(card.querySelectorAll('[class]'))) {
      if (NAME.test(el.className) && (el.textContent ?? '').trim().length >= 4) {
        out.title = stableSelector(el, card);
        break;
      }
    }
  }

  // ── price ──────────────────────────────────────────────────────
  const PRICE_TXT = /[£$€]\s*\d|\d+\.\d{2}|\d+\s?(?:p|pence)\b/i;
  const PRICE_CLS = /\b(price|cost|amount|curprice|s-largered)\b/i;
  for (const el of Array.from(card.querySelectorAll('[class]'))) {
    if (PRICE_CLS.test(el.className) && PRICE_TXT.test(el.textContent ?? '')) {
      out.price = stableSelector(el, card);
      break;
    }
  }

  // ── url ────────────────────────────────────────────────────────
  const BAD_HREF = /wishlist|cart|signin|login|account|customerservices|wishl|share/i;
  for (const a of Array.from(card.querySelectorAll('a[href]'))) {
    const href = a.getAttribute('href') ?? '';
    if (!href || href.startsWith('#') || BAD_HREF.test(href)) continue;
    out.url = stableSelector(a, card);
    break;
  }

  // ── id ─────────────────────────────────────────────────────────
  // Card-root attribute first (data-product-id / data-sku / data-pid).
  const ID_ATTRS = ['data-product-id', 'data-sku', 'data-pid', 'data-id', 'data-itemid', 'id'];
  for (const a of ID_ATTRS) {
    const v = card.getAttribute(a);
    if (v && /^[A-Za-z0-9_-]{3,}$/.test(v)) {
      out.id = `[${a}]`;
      break;
    }
  }

  return out;
}

/** Build a class-based selector for `el` relative to `root`. Picks the
 *  first non-trivial class so the result survives DOM reflow. Falls
 *  back to the bare tag if `el` has no usable class. */
function stableSelector(el: Element, root: Element): string {
  void root; // reserved — descendant path could be added when uniqueness drifts
  const tag = el.tagName.toLowerCase();
  const cls = (el.className || '').trim().split(/\s+/).filter(Boolean);
  // Drop classes that look like build-time hashes (e.g. `css-1ab2c3d`,
  // `jsx-1234567890`, or any single-letter class).
  const stable = cls.filter((c) => c.length >= 3 && !/^(css|jsx|sc|tw|app)-?[0-9a-f-]{5,}$/i.test(c) && !/^\d+$/.test(c));
  if (stable.length === 0) return tag;
  return `${tag}.${stable[0]}`;
}

function groupKey(el: Element): string {
  const tag = el.tagName.toLowerCase();
  const cls = (el.className || '').trim().split(/\s+/).filter(Boolean);
  if (cls.length === 0) return '';
  // Use the first ~2 classes as the grouping key — captures BEM
  // root + modifier without overfitting to noise classes.
  const lead = cls.slice(0, 2).join('.');
  return `${tag}.${lead}`;
}

function containsPriceText(text: string): boolean {
  // Loose match: "£12.99", "$1,299", "€19,90", "12.99 USD". Avoids
  // false positives on plain numbers by requiring a currency anchor.
  const re = /(£|\$|€|¥|GBP|USD|EUR|JPY|CHF)\s?\d{1,4}([.,]\d{2})?\b|\b\d{1,4}[.,]\d{2}\s?(£|\$|€|GBP|USD|EUR)/i;
  return re.test(text.replace(/\s+/g, ' ').slice(0, 4000));
}

// ── Cookie-banner detector ────────────────────────────────────────

interface CookieProvider {
  name:                string;
  containerSelectors:  string[];
  acceptSelectors:     string[];
}

/** Provider catalogue — same set humanevasion's cookies_db.go knows
 *  about, in order of detection priority (most distinctive first).
 *  Add a new provider here AND in cookies_db.go to keep
 *  frontend-detect + runner-dismiss in lock-step. */
const PROVIDERS: ReadonlyArray<CookieProvider> = [
  {
    name: 'OneTrust',
    containerSelectors: ['#onetrust-banner-sdk', '#onetrust-consent-sdk'],
    acceptSelectors:    ['#onetrust-accept-btn-handler', '.onetrust-accept-btn-handler'],
  },
  {
    name: 'Cookiebot',
    containerSelectors: ['#CybotCookiebotDialog'],
    acceptSelectors:    ['#CybotCookiebotDialogBodyButtonAccept'],
  },
  {
    name: 'TrustArc',
    containerSelectors: ['#truste-consent-track', '.truste_box_overlay'],
    acceptSelectors:    ['.call', '#truste-consent-button'],
  },
  {
    name: 'Klaro',
    containerSelectors: ['.klaro', '.cookie-modal'],
    acceptSelectors:    ['.cm-btn-success', '.cm-btn-accept'],
  },
  {
    name: 'Quantcast',
    containerSelectors: ['.qc-cmp2-container', '#qc-cmp2-ui'],
    acceptSelectors:    ['button[mode=primary]', '.qc-cmp2-button[mode=primary]'],
  },
  {
    name: 'Didomi',
    containerSelectors: ['#didomi-host', '.didomi-popup-container'],
    acceptSelectors:    ['#didomi-notice-agree-button'],
  },
  {
    name: 'Generic-cookie',
    // Last-resort match for sites that roll their own banner. Looks
    // for any element with class containing "cookie" + an accept-ish
    // button. Confidence will be lower than the named providers.
    containerSelectors: ['[class*="cookie" i]', '[id*="cookie" i]'],
    acceptSelectors:    ['button[id*="accept" i]', 'button[class*="accept" i]'],
  },
];

export function detectCookieBanner(html: string): CookieBannerHit | null {
  const doc = parseHtml(html);
  if (!doc) return null;

  for (const p of PROVIDERS) {
    const containerSel = p.containerSelectors.find((s) => doc.querySelector(s));
    if (!containerSel) continue;
    const acceptSel = p.acceptSelectors.find((s) => doc.querySelector(s));
    if (!acceptSel) continue;
    return {
      provider:          p.name,
      acceptSelector:    acceptSel,
      containerSelector: containerSel,
      confidence:        p.name === 'Generic-cookie' ? 50 : 90,
    };
  }
  return null;
}

// ── JS-render need detector ───────────────────────────────────────

/**
 * Compares the body sizes of an HTTP-engine response vs a browser-engine
 * rendered HTML. If the browser version is significantly larger, the
 * page hydrates more content client-side and a browser engine is
 * required.
 *
 * Heuristic:
 *   - sizeRatio = browserHtml.length / max(httpBody.length, 1)
 *   - required = sizeRatio > 1.5 OR
 *                browser has many listing cards but http has none
 *
 * Caller passes the LARGEST captured body for each side (we don't
 * try to be smart about which engine produced what — just pick the
 * largest HTTP response and the largest browser response).
 */
export function detectJsRenderNeed(
  httpBody:    string,
  browserHtml: string,
  httpListingCount = -1,
  browserListingCount = -1,
): JsRenderVerdict {
  const reasons: string[] = [];
  const httpLen = httpBody.length;
  const brLen   = browserHtml.length;
  const ratio   = brLen / Math.max(httpLen, 1);

  if (httpLen === 0 && brLen === 0) {
    return { required: false, reasons: ['no body to compare'], sizeRatio: 0 };
  }
  if (httpLen === 0) {
    reasons.push('HTTP engine returned empty body; only browser produced HTML');
    return { required: true, reasons, sizeRatio: ratio };
  }

  let required = false;
  if (ratio >= 1.5) {
    required = true;
    reasons.push(`browser HTML ${ratio.toFixed(1)}× larger than HTTP body`);
  }
  if (httpListingCount === 0 && browserListingCount > 0) {
    required = true;
    reasons.push(`browser found ${browserListingCount} cards, HTTP found 0`);
  }
  if (!required) {
    reasons.push(`HTTP body and browser HTML similar size (ratio ${ratio.toFixed(2)})`);
  }
  return { required, reasons, sizeRatio: ratio };
}

// ── Pagination detector ───────────────────────────────────────────

const PAGINATION_PARAMS = ['page', '_pgn', 'p', 'offset', 'start', 'cursor', 'pg'];
const NEXT_TEXT_RE = /^\s*(next|next page|load more|show more|see more|more|»|>|→)\s*$/i;

export function detectPagination(html: string, _baseUrl?: string): PaginationHit | null {
  const doc = parseHtml(html);
  if (!doc) return null;

  // 1. Look for anchor hrefs with pagination-style query params.
  const links = Array.from(doc.querySelectorAll('a[href]'));
  for (const a of links) {
    const href = a.getAttribute('href') ?? '';
    if (!href) continue;
    try {
      // URL parser handles both absolute and relative against a base.
      const u = new URL(href, 'http://x/');
      for (const p of PAGINATION_PARAMS) {
        if (u.searchParams.has(p)) {
          return {
            type:       p === 'offset' || p === 'start' ? 'offset-param' : (p === 'cursor' ? 'cursor-param' : 'page-param'),
            paramName:  p,
            confidence: 80,
          };
        }
      }
    } catch { /* malformed href — keep scanning */ }
  }

  // 2. Button or anchor with text like "Next", "Load more".
  const clickables = Array.from(doc.querySelectorAll('a, button'));
  for (const el of clickables) {
    const txt = (el.textContent ?? '').trim();
    if (!txt || txt.length > 30) continue;
    if (NEXT_TEXT_RE.test(txt)) {
      const cls = el.className || '';
      const hasLoadMoreClass = /load[-_]?more|show[-_]?more/i.test(cls);
      return {
        type:       hasLoadMoreClass ? 'load-more' : 'next-button',
        selector:   buildButtonSelector(el),
        confidence: 65,
      };
    }
  }

  return null;
}

function buildButtonSelector(el: Element): string {
  const tag = el.tagName.toLowerCase();
  const cls = (el.className || '').trim().split(/\s+/).filter(Boolean);
  if (cls.length > 0) return `${tag}.${cls.slice(0, 2).join('.')}`;
  const id = el.getAttribute('id');
  if (id) return `${tag}#${id}`;
  const text = (el.textContent ?? '').trim().slice(0, 20);
  return `${tag}:has-text("${text}")`;  // fallback, not standard CSS — UI shows it as advisory
}

// ── Tier recommendation ──────────────────────────────────────────

/** Picks the lightest humanevasion tier likely to defeat the
 *  observed block signals. Prefers 'js' (cheapest) when no soft/
 *  hard blocks are seen, escalates as evidence demands.
 *
 *  Lock-step with humanevasion's tier semantics: js < cdp < vm.
 */
export function recommendHumanevasionTier(
  verdict: 'ok' | 'soft_block' | 'hard_block' | string,
  blockSignals: Array<{ id: string; severity: string }>,
): HumanevasionTier {
  if (verdict === 'hard_block')                      return 'vm';
  // Akamai BMP / PerimeterX have historically required vm-tier.
  if (blockSignals.some((s) => s.id === 'akamai_bmp_splashui'))  return 'vm';
  if (blockSignals.some((s) => s.id === 'perimeterx'))           return 'vm';
  if (verdict === 'soft_block')                      return 'cdp';
  return 'cdp'; // safe default — js tier is occasionally rejected by CSP-strict sites
}

// ── JSON detector ─────────────────────────────────────────────────

/** Resolve a dot/bracket path against an arbitrary JSON value.
 *  Supports dot notation (`a.b.c`), numeric indices (`a[0]`), and
 *  the JMESPath-style flatten suffix (`a[*]` is treated as `a` —
 *  we already iterate). Returns undefined on miss.
 *
 *  Deliberately tiny — a full JMESPath engine would be ~30KB; the
 *  shapes we hit in practice are 1–3 segments deep. */
function resolveJsonPath(root: unknown, path: string): unknown {
  if (!path) return root;
  const norm = path.replace(/\[\*\]/g, '').trim();
  if (!norm) return root;
  const parts = norm.split(/\.|\[(\d+)\]/).filter((p) => p !== undefined && p !== '');
  let cur: unknown = root;
  for (const p of parts) {
    if (cur == null) return undefined;
    if (Array.isArray(cur)) {
      const idx = Number(p);
      if (!Number.isFinite(idx)) return undefined;
      cur = cur[idx];
    } else if (typeof cur === 'object') {
      cur = (cur as Record<string, unknown>)[p];
    } else {
      return undefined;
    }
  }
  return cur;
}

/** Walk the JSON tree breadth-first and return paths whose value is
 *  a non-empty array of objects. Capped to keep the search bounded
 *  on pathological payloads. */
function findArrayPaths(root: unknown, maxDepth = 5, maxPaths = 12): string[] {
  const out: string[] = [];
  const queue: Array<{ value: unknown; path: string; depth: number }> = [
    { value: root, path: '', depth: 0 },
  ];
  while (queue.length > 0 && out.length < maxPaths) {
    const { value, path, depth } = queue.shift()!;
    if (depth > maxDepth || value == null) continue;
    if (Array.isArray(value)) {
      if (value.length > 0 && typeof value[0] === 'object' && value[0] !== null) {
        out.push(path || '$');
      }
      continue; // don't recurse into array elements for path-finding
    }
    if (typeof value === 'object') {
      for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
        queue.push({ value: v, path: path ? `${path}.${k}` : k, depth: depth + 1 });
      }
    }
  }
  return out;
}

/** Common product-key heuristics — first match wins. Lowercase
 *  comparisons against the union of observed keys. */
const TITLE_KEYS = ['title', 'name', 'productname', 'displayname', 'boxname'];
const PRICE_KEYS = ['price', 'sellprice', 'sellpriceinc', 'cashprice', 'amount', 'cost'];
const IMAGE_KEYS = ['image', 'imageurl', 'imageurls', 'thumbnail', 'imagesrc', 'imageurlmedium'];
const URL_KEYS   = ['url', 'producturl', 'link', 'permalink', 'href'];
const ID_KEYS    = ['id', 'sku', 'productid', 'boxid', 'asin', 'itemid'];

/** Normalize a key for matching: lowercase + strip non-alphanumerics
 *  so `image_url`, `imageURL`, `image-url` all collapse to `imageurl`
 *  and match the candidate list. */
function normalizeKey(k: string): string {
  return k.toLowerCase().replace(/[^a-z0-9]/g, '');
}

function pickFieldGuess(keys: string[]): JsonProfile['fieldGuess'] {
  const norm = new Map(keys.map((k) => [normalizeKey(k), k]));
  const find = (cands: string[]) => cands.map((c) => norm.get(c)).find(Boolean);
  return {
    id:    find(ID_KEYS),
    title: find(TITLE_KEYS),
    price: find(PRICE_KEYS),
    image: find(IMAGE_KEYS),
    url:   find(URL_KEYS),
  };
}

/** Sniff pagination params on the request URL. We don't have the
 *  response shape's pagination here (e.g. `nextCursor`) — that's
 *  visible in `itemKeys` and shown to the operator. */
function detectJsonPagination(url: string): PaginationHit | null {
  try {
    const u = new URL(url);
    for (const p of PAGINATION_PARAMS) {
      if (u.searchParams.has(p)) {
        return {
          type:       p === 'offset' || p === 'start' ? 'offset-param' : (p === 'cursor' ? 'cursor-param' : 'page-param'),
          paramName:  p,
          confidence: 75,
        };
      }
    }
  } catch { /* malformed url */ }
  return null;
}

export interface DetectJsonProfileInput {
  url:           string;
  /** Raw response body. May be a fragment of a larger JSON payload
   *  if it came from `bodyPreview` (4KB cap). */
  body:          string;
  /** Operator-supplied path. When undefined/empty, we BFS-search the
   *  tree for the first non-empty array of objects. */
  productsPath?: string;
}

export function detectJsonProfile(input: DetectJsonProfileInput): JsonProfile | null {
  if (!input.body || input.body.length < 2) return null;
  let root: unknown;
  try {
    root = JSON.parse(input.body);
  } catch {
    // Body may be truncated (4KB cap on bodyPreview) — try recovering
    // the longest valid prefix by trimming until parse succeeds. Cheap
    // because parsing fails fast.
    let trimmed = input.body;
    let parsed = false;
    for (let i = 0; i < 4 && trimmed.length > 100; i++) {
      trimmed = trimmed.slice(0, Math.floor(trimmed.length * 0.8));
      try { root = JSON.parse(trimmed); parsed = true; break; } catch { /* keep trimming */ }
    }
    if (!parsed) return null;
  }

  const reasons: string[] = [];
  let listPath = (input.productsPath ?? '').trim();
  let arr: unknown;
  let pathSource: 'user' | 'guessed' = 'user';

  if (listPath) {
    arr = resolveJsonPath(root, listPath);
    if (!Array.isArray(arr) || arr.length === 0) {
      reasons.push(`user path "${listPath}" did not resolve to a non-empty array — falling back to BFS`);
      listPath = '';
    }
  }
  if (!listPath) {
    pathSource = 'guessed';
    const candidates = findArrayPaths(root);
    if (candidates.length === 0) return null;
    listPath = candidates[0];
    arr = resolveJsonPath(root, listPath === '$' ? '' : listPath);
    reasons.push(`auto-picked first non-empty object array at "${listPath}"${candidates.length > 1 ? ` (${candidates.length - 1} other candidates)` : ''}`);
  }

  if (!Array.isArray(arr) || arr.length === 0) return null;

  // Union of keys across the first 5 items.
  const sample = arr.slice(0, 5).filter((x): x is Record<string, unknown> => typeof x === 'object' && x !== null);
  const keySet = new Set<string>();
  for (const item of sample) {
    for (const k of Object.keys(item)) keySet.add(k);
  }
  const itemKeys = Array.from(keySet).sort();
  const fieldGuess = pickFieldGuess(itemKeys);
  reasons.push(`${arr.length} items · ${itemKeys.length} keys observed`);
  if (fieldGuess.title) reasons.push(`title key: ${fieldGuess.title}`);
  if (fieldGuess.price) reasons.push(`price key: ${fieldGuess.price}`);

  // Confidence: 100 base, –20 if we guessed the path, –10 if <3 items,
  // –10 if no title key found.
  let confidence = 100;
  if (pathSource === 'guessed') confidence -= 20;
  if (arr.length < 3) confidence -= 10;
  if (!fieldGuess.title) confidence -= 10;
  confidence = Math.max(0, Math.min(100, confidence));

  const sampleItem = JSON.stringify(sample[0] ?? arr[0], null, 2).slice(0, 800);

  return {
    listPath:   listPath === '$' ? '' : listPath,
    count:      arr.length,
    itemKeys,
    fieldGuess,
    sampleItem,
    confidence,
    reasons,
  };
}

// ── Orchestrator ──────────────────────────────────────────────────

export interface BuildProfileInput {
  url:                  string;
  recommendedEngine:    string;
  /** Best-effort: HTML body from the HTTP engine (direct_http or cf_http). May be empty. */
  httpBody:             string;
  /** Best-effort: rendered HTML from a browser engine. May be empty if no browser engine ran. */
  browserHtml:          string;
  /** Best-effort: listing card count if known from a prior detection. */
  httpListingCount?:    number;
  browserListingCount?: number;
  /** From the recommended engine's ProbeResult. */
  blockVerdict:         string;
  blockSignals:         Array<{ id: string; severity: string }>;
  /** Page type — drives whether HTML or JSON heuristics fire.
   *  Defaults to 'html' for backward compatibility with callers
   *  that haven't been updated. */
  pageType?:            ProfilePageType;
  /** Operator-supplied JMESPath-ish path for JSON profiles. */
  productsPath?:        string;
}

export function buildProfile(input: BuildProfileInput): SiteProfile {
  const pageType = input.pageType ?? 'html';
  const tier = recommendHumanevasionTier(input.blockVerdict, input.blockSignals);

  if (pageType === 'json') {
    // JSON branch — skip HTML heuristics entirely. Browser HTML is
    // typically empty for JSON endpoints (browser engines won't have
    // been useful), so we fall back to httpBody.
    const body = input.httpBody || input.browserHtml || '';
    const json = detectJsonProfile({ url: input.url, body, productsPath: input.productsPath });
    const paging = detectJsonPagination(input.url);

    // Completeness for JSON: 40 list-resolved + 20 title-key + 20 paging
    // + 20 recommended-engine. Below 60 → manual review.
    let completeness = 0;
    if (json)                    completeness += 40;
    if (json?.fieldGuess.title)  completeness += 20;
    if (paging != null)          completeness += 20;
    if (input.recommendedEngine) completeness += 20;

    return {
      url:               input.url,
      pageType:          'json',
      recommendedEngine: input.recommendedEngine,
      listingCard:       null,
      cookieBanner:      null,
      jsRenderNeed:      { required: false, reasons: ['JSON endpoint — no JS render needed'], sizeRatio: 0 },
      pagination:        paging,
      recommendedTier:   tier,
      json,
      completeness,
    };
  }

  // HTML branch — original behavior.
  // Prefer browser HTML for selector discovery — that's the post-JS
  // DOM the actual scraper will see. Fall back to HTTP body only
  // when no browser engine ran.
  const html = input.browserHtml || input.httpBody || '';
  const cards   = detectListingCards(html);
  const banner  = detectCookieBanner(html);
  const jsNeed  = detectJsRenderNeed(input.httpBody, input.browserHtml, input.httpListingCount, input.browserListingCount);
  const paging  = detectPagination(html, input.url);

  // Completeness — coarse scoring so the UI can flag profiles that
  // need manual review. Each discovered field carries 25 pts.
  let completeness = 0;
  if (cards.length > 0)  completeness += 25;
  if (banner != null)    completeness += 25;
  if (paging != null)    completeness += 25;
  // jsRenderNeed is always populated (returns a verdict either way),
  // so the last 25 pts go on the recommended-engine being available.
  if (input.recommendedEngine) completeness += 25;

  return {
    url:                input.url,
    pageType:           'html',
    recommendedEngine:  input.recommendedEngine,
    listingCard:        cards[0] ?? null,
    cookieBanner:       banner,
    jsRenderNeed:       jsNeed,
    pagination:         paging,
    recommendedTier:    tier,
    json:               null,
    completeness,
  };
}

// ── helpers ────────────────────────────────────────────────────────

/**
 * Parse an HTML string with DOMParser. Returns null on parse error
 * or in non-browser environments (e.g. SSR / Node tests if any).
 * Caller treats null as "no signal" — same shape as a clean miss,
 * which keeps callers from needing try/catch wrappers.
 */
function parseHtml(html: string): Document | null {
  if (typeof DOMParser === 'undefined') return null;
  try {
    return new DOMParser().parseFromString(html, 'text/html');
  } catch {
    return null;
  }
}
